<template>
  <div ref="rootEl" class="mb-4 flex w-full flex-nowrap items-center gap-2">
    <tiny-user-thumb
      v-if="!simple"
      :key="`${props.photoDoc._id}`"
      :uuid="authorId"
    />
    <InformationText v-if="photoDoc.reported_by?.length > 0" color="yellow">
      <p class="text-center font-medium">
        {{ $t("components.report.hasBeenReportedBy") }}
        {{ reportedNames }}.
        {{ $t("components.report.adminsWillDetermine") }}
      </p>
    </InformationText>
    <div
      v-else
      class="relative grow overflow-hidden rounded-lg border-2 border-solid border-black-dark bg-white shadow-purple-hard dark:bg-slate-600"
    >
      <div
        v-if="canEdit && !simple"
        class="absolute left-2 top-2 z-20 transform cursor-pointer rounded-full border-2 border-kekkle-dark bg-white p-1 shadow-purple-hard-sm duration-300 hover:-translate-y-1 hover:scale-105 dark:bg-slate-700"
        @click="goToEditView()"
      >
        <pencil-alt-icon
          class="h-4 w-4 flex-none text-kekkle-dark dark:text-white"
        ></pencil-alt-icon>
      </div>
      <div
        v-if="!simple"
        class="absolute right-2 top-2 z-20 transform cursor-pointer rounded-full border-2 border-kekkle-dark bg-white p-1 shadow-purple-hard-sm duration-300 hover:-translate-y-1 hover:scale-105 dark:bg-slate-700"
        @click="reportModalIsOpen = true"
      >
        <dots-vertical-icon
          class="h-4 w-4 flex-none text-kekkle-dark dark:text-white"
        ></dots-vertical-icon>
      </div>

      <!-- Image area: the blurhash always sits behind, the real photo is keyed
           by its URL and layered on top. Keying forces Vue to replace the node
           per photo, so a recycled scroller view never paints the previous
           photo's bitmap (no "wrong image" flash). -->
      <div
        class="relative w-full"
        :class="simple ? 'aspect-video' : 'aspect-square'"
      >
        <img
          v-if="hashUrl"
          alt="Foto"
          :src="hashUrl"
          class="absolute inset-0 h-full w-full object-cover object-center"
        />
        <img
          v-if="imageUrl"
          :key="imageUrl"
          alt="Foto"
          :src="imageUrl"
          class="absolute inset-0 h-full w-full object-cover object-center"
          @click="openImage"
        />
      </div>
      <div
        v-if="caption"
        class="overflow-hidden p-2 italic dark:bg-kekkle-dark-light dark:text-slate-200"
      >
        {{ caption }}
      </div>

      <div
        class="px-2 text-right italic dark:bg-kekkle-dark-light dark:text-slate-200"
      >
        {{ timeAgoString }}
      </div>
      <div
        class="px-2 pb-2 text-right text-xs italic text-gray-500 dark:bg-kekkle-dark-light dark:text-slate-400"
      >
        {{ timeString }}
      </div>
    </div>

    <div
      v-if="!simple"
      class="mx-1 flex flex-col justify-center"
      @click="goToComments"
    >
      <chat-alt2-icon
        class="w-6 transform duration-300 hover:-translate-y-2 dark:text-slate-200"
      />
      <p class="text-center text-lg font-medium dark:text-slate-200">
        {{ photoDoc.number_of_comments ?? 0 }}
      </p>
    </div>
    <Teleport v-if="reportModalIsOpen" to="body">
      <ConformationModal
        safe-mode
        :visible="reportModalIsOpen"
        :confirm-text="$t('components.report.report')"
        :cancel-text="$t('components.report.close')"
        :loading="reporting"
        @cancel="reportModalIsOpen = false"
        @confirm="reportPhoto"
      >
        <h3 class="text-xl font-bold dark:text-slate-200">
          {{ $t("components.report.reportTitle") }}
        </h3>
        <p class="my-2">
          {{ $t("components.report.reportText") }}
        </p>
        <information-text
          class="my-2"
          color="orange"
          loa
          :information="$t('components.report.reportInformation')"
        ></information-text>
      </ConformationModal>
    </Teleport>
  </div>
  <Teleport v-if="fullscreen && !simple" to="body">
    <div
      v-if="fullscreen && !simple"
      class="max-h-screen max-w-screen fixed left-0 top-0 z-50 flex min-h-screen w-screen flex-col items-center justify-center overflow-scroll bg-slate-700 bg-opacity-60"
      @click="closeImage"
    >
      <div class="h-full w-fit max-w-lg">
        <img
          v-if="imageUrl"
          ref="zoomImage"
          alt="Foto-big"
          :src="imageUrl"
          style="max-height: 50vh"
          class="h-full overflow-visible rounded object-contain"
          @click.stop
          @touchstart="touchStart"
          @touchmove="touchMove"
        />
        <div
          v-if="!hasImageQuality"
          class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-kekkle-dark-light"
          @click.stop
        >
          <div
            class="quality-loading-bar h-full rounded-full bg-kekkle-purple"
          />
        </div>
        <div
          class="flex w-full max-w-lg items-center justify-between rounded-lg bg-white p-2 dark:bg-kekkle-dark"
          @click.stop
        >
          <div class="flex gap-1.5">
            <button
              v-for="option in qualityOptions"
              :key="option.value"
              class="rounded-lg border-2 border-black-dark px-3 py-1 text-sm font-medium transition-all"
              :class="
                selectedQuality === option.value
                  ? 'bg-kekkle-purple text-white shadow-purple-hard-sm'
                  : 'bg-white text-kekkle-dark hover:bg-gray-100 dark:bg-kekkle-dark-light dark:text-slate-200 dark:hover:bg-kekkle-dark-dark'
              "
              @click="selectedQuality = option.value"
            >
              {{ option.label }}
            </button>
          </div>
          <a
            v-if="!isFlutter"
            :href="imageUrl"
            :download="fileSafeCaption"
            class="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-black-dark bg-white transition-all hover:shadow-purple-hard-sm active:shadow-none dark:bg-kekkle-dark-light"
            :aria-label="$t('pages.photo.download')"
          >
            <DownloadIcon class="h-5 w-5 text-kekkle-purple" />
          </a>
          <button
            v-else
            class="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-black-dark bg-white transition-all hover:shadow-purple-hard-sm active:shadow-none dark:bg-kekkle-dark-light"
            :aria-label="$t('pages.photo.download')"
            @click="postImageToFlutter"
          >
            <DownloadIcon class="h-5 w-5 text-kekkle-purple" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup>
import {
  PencilAltIcon,
  ChatAlt2Icon,
  DotsVerticalIcon,
  DownloadIcon,
} from "@heroicons/vue/outline";
import { computed, defineProps, ref, watch, defineEmits } from "vue";
import { ImageSizes } from "@/enums/images-sizes";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { isRunningInFlutter } from "@/helpers/flutter/capabilities";
import { saveFileFlutter } from "@/helpers/flutter/features/sharing.js";
import { usePhotoManagement } from "@/composables/usePhotoManagement";
import { useElementVisibility } from "@/composables/useElementVisibility";

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const photosStore = usePhotosStore();
const userStore = useUserStore();
const { openEditPhotoBottomSheet } = usePhotoManagement();

import TinyUserThumb from "../components/TinyUserThumb.vue";
import { decodeBlurhashToUrl } from "@/helpers/blurhash/decodeBlurhashToUrl";
import { timeAgo } from "@/helpers/dates/time-ago";
import { dateAgo } from "@/helpers/dates/date-ago";
import { getPhotoFromFirestorage } from "@/helpers/firestorage/PhotoPictureService";
import { setErrorToast, setSuccessToast } from "@/helpers/notifications/toast";
import InformationText from "@/components/InformationText.vue";
import ConformationModal from "@/components/Modals/ConformationModal.vue";
import { translate } from "@/i18n/vueI18n";
import { useUserStore } from "@/stores/modules/user/user.js";
import { formatMemberNamesList } from "@/helpers/utils/format-member-names";

const fullscreen = ref(false);
const zoomImage = ref(null);
const emits = defineEmits(["goToComments", "edit"]);

const isFlutter = isRunningInFlutter();

const qualityOptions = [
  { value: ImageSizes.HD, label: "720p" },
  { value: ImageSizes.Full_hd, label: "1080p" },
  { value: ImageSizes.Ultra_hd, label: "4K" },
];

let start = ref({
  x: 0,
  y: 0,
  dx: 0,
  dy: 0,
  distance: 0,
  currentScale: 1,
});

let selectedQuality = ref(ImageSizes.SD);
let reportModalIsOpen = ref(false);
let reporting = ref(false);

watch(selectedQuality, () => {
  downloadImage();
});

const props = defineProps({
  photoDoc: null,
  simple: {
    required: false,
    default: false,
  },
});

const hashUrl = computed(() => decodeBlurhashToUrl(props.photoDoc?.blurhash));
let imageData = ref({});

const date = computed(() => {
  return props.photoDoc?.timestamp;
});

const reportedNames = computed(() => {
  if (!groupsStore.groupData?.members) {
    return "";
  }
  return formatMemberNamesList(
    props.photoDoc.reported_by,
    groupsStore.groupData.members,
    translate("global.deleted_user"),
  );
});

const caption = computed(() => {
  return props.photoDoc?.caption;
});

//a computed property with the caption without emoji
const emoijStrippedCaption = computed(() => {
  if (!caption.value) {
    return "";
  }
  return caption.value.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "");
});

const fileSafeCaption = computed(() => {
  if (emoijStrippedCaption.value) {
    return emoijStrippedCaption.value;
  } else if (date.value) {
    return date.value.toTimeString();
  } else {
    return new Date().toTimeString();
  }
});

const groupDocId = computed(() => {
  return userStore.activeGroup;
});

// SD image is owned by the photos store (loaded once, kept across remounts).
const sdImage = computed(() =>
  photosStore.getPhotoImage(
    groupDocId.value,
    props.photoDoc?._id,
    ImageSizes.SD,
  ),
);

const imageUrl = computed(() => {
  return (
    imageData.value[selectedQuality.value]?.url ??
    imageData.value[ImageSizes.SD]?.url ??
    sdImage.value?.url
  );
});

const image = computed(() => {
  return (
    imageData.value[selectedQuality.value] ??
    imageData.value[ImageSizes.SD] ??
    (sdImage.value
      ? { data: sdImage.value.blob, url: sdImage.value.url }
      : undefined)
  );
});

const hasImageQuality = computed(() => {
  return !!imageData.value[selectedQuality.value]?.url;
});

const createdBy = computed(() => {
  return props.photoDoc?.created_by;
});

const canEdit = computed(() => {
  return groupsStore.isAdmin || createdBy.value === authStore.authUuid;
});

function goToComments() {
  emits("goToComments", {
    photoId: props.photoDoc._id,
    groupId: userStore.activeGroup,
  });
}

async function postImageToFlutter() {
  if (!image.value.data) {
    return;
  }
  const reader = new FileReader();
  await reader.readAsDataURL(image.value.data);
  reader.onloadend = function () {
    const base64data = reader.result;
    saveFileFlutter(
      base64data.toString(),
      fileSafeCaption.value,
      groupsStore.groupData?.name ?? "",
      "pages.photo.file_saved",
    );
  };
}

function openImage() {
  if (selectedQuality.value === ImageSizes.SD) {
    selectedQuality.value = ImageSizes.HD;
  }
  fullscreen.value = true;
}

function closeImage() {
  fullscreen.value = false;
  zoomImage.value.style.transform = "";
  zoomImage.value.style.WebkitTransform = "";
  zoomImage.value.style.zIndex = "";
  start.value = {
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    distance: 0,
    currentScale: 1,
  };
}

// Load the SD image lazily: only once the photo is on (or near) the screen.
const rootEl = ref(null);
const isVisible = useElementVisibility(rootEl);

watch(
  [isVisible, () => props.photoDoc?._id],
  ([visible]) => {
    if (!visible || !groupDocId.value) return;
    photosStore.loadPhotoImage(groupDocId.value, props.photoDoc);
  },
  { immediate: true },
);

// Reset per-photo state when the photo changes (e.g. DynamicScroller recycling).
// The visibility watcher above re-triggers the SD load for the new photo.
watch(
  () => props.photoDoc?._id,
  () => {
    imageData.value = {};
    selectedQuality.value = ImageSizes.SD;
  },
);

function goToEditView() {
  emits("edit");
  openEditPhotoBottomSheet(props.photoDoc);
}

function distance(event) {
  return Math.hypot(
    event.touches[0].pageX - event.touches[1].pageX,
    event.touches[0].pageY - event.touches[1].pageY,
  );
}

function touchStart(event) {
  if (event.touches.length === 2) {
    event.preventDefault(); // Prevent page scroll
    // Calculate where the fingers have started on the X and Y axis
    start.value.x =
      (event.touches[0].pageX + event.touches[1].pageX) / 2 - start.value.dx;
    start.value.y =
      (event.touches[0].pageY + event.touches[1].pageY) / 2 - start.value.dy;
    start.value.distance = distance(event);
  }
}

function touchMove(event) {
  if (event.touches.length === 2) {
    event.preventDefault(); // Prevent page scroll
    let scale;
    // Safari provides event.scale as two fingers move on the screen
    // For other browsers just calculate the scale manually
    if (event.scale) {
      scale = event.scale;
    } else {
      scale = distance(event) / start.value.distance;
    }

    let imageElementScale = Math.min(
      Math.max(0.8, scale * start.value.currentScale),
      10,
    );

    // Calculate how much the fingers have moved on the X and Y axis
    const deltaX =
      (event.touches[0].pageX + event.touches[1].pageX) / 2 - start.value.x; // x2 for accelarated movement
    const deltaY =
      (event.touches[0].pageY + event.touches[1].pageY) / 2 - start.value.y; // x2 for accelarated movement
    start.value.dx = deltaX;
    start.value.dy = deltaY;

    start.value.distance = distance(event);
    start.value.currentScale = imageElementScale;

    // Transform the image to make it grow and move with fingers
    const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`;

    zoomImage.value.style.transform = transform;
    zoomImage.value.style.WebkitTransform = transform;
    zoomImage.value.style.zIndex = "9999";
  }
}

async function reportPhoto() {
  try {
    reporting.value = true;

    // Report the photo via the store (handles Firestore update and refetch)
    await photosStore.reportPhoto(props.photoDoc._id);

    setSuccessToast({
      message: translate("components.report.toastReported"),
    });
  } catch (error) {
    console.error("Error reporting photo:", error);
    setErrorToast({
      message: translate("components.report.toastNotReported"),
    });
  } finally {
    reportModalIsOpen.value = false;
    reporting.value = false;
  }
}

async function downloadImage() {
  // The SD (list) quality is loaded lazily through the store once the photo
  // scrolls into view (see the visibility watcher below). Higher qualities are
  // fetched here on demand when the fullscreen viewer requests them.
  if (selectedQuality.value === ImageSizes.SD) {
    return;
  }

  if (imageData.value?.[selectedQuality.value]?.url) {
    return;
  }

  try {
    const result = await getPhotoFromFirestorage(
      groupDocId.value,
      props.photoDoc._id,
      props.photoDoc?.picture_url,
      selectedQuality.value,
      props.photoDoc?.blurhash,
    );

    imageData.value[selectedQuality.value] = {
      data: result.blob,
      url: result.url,
    };
  } catch (error) {
    console.error("Error downloading photo:", error);
    // If the specific format fails, try loading Ultra_hd (original)
    try {
      const result = await getPhotoFromFirestorage(
        groupDocId.value,
        props.photoDoc._id,
        props.photoDoc?.picture_url,
        ImageSizes.Ultra_hd,
        props.photoDoc?.blurhash,
      );

      imageData.value[ImageSizes.Ultra_hd] = {
        data: result.blob,
        url: result.url,
      };
      selectedQuality.value = ImageSizes.Ultra_hd;
    } catch (fallbackError) {
      console.error("Error downloading original photo:", fallbackError);
    }
  }
}

const authorId = computed(() => props.photoDoc?.created_by);
const timeString = computed(() => (date.value ? dateAgo(date.value) : ""));
const timeAgoString = computed(() => (date.value ? timeAgo(date.value) : ""));
</script>
<style lang="scss">
.quality-loading-bar {
  width: 40%;
  animation: indeterminate 1.2s ease-in-out infinite;
}

@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
</style>
